import { Theme } from '@mui/material'
import { makeStyles, createStyles } from '@mui/styles'
import { memo } from 'react'

import {
  convertToGermanCurrencyFormat,
  convertToGermanLocale,
} from '../../utils/banklist'
import BankRate from './decorators/bank-rate'
import { IBankListItemProps, RATE_AFFIX } from './typings'
import BankListCTA from './decorators/BankListCTA'
import BankLogoWithRating from './decorators/bank-logo-with-rating'
import theme from '../../theme'

const useStyles = makeStyles<Theme, { isRowExpanded: boolean }>(
  (muiTheme: Theme) =>
    createStyles({
      bankListItemContainer: {
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${muiTheme.palette.grey[400]}`,
        borderRadius: ({ isRowExpanded }) =>
          isRowExpanded ? '5px 5px 0px 0px' : 5,
        borderBottom: ({ isRowExpanded }) =>
          isRowExpanded
            ? `1px solid ${muiTheme.palette.grey[200]}`
            : `1px solid ${muiTheme.palette.grey[500]}`,
        width: '100%',
        padding: '0 15px',
        [theme.breakpoints.down('md')]: {
          padding: '0 5px',
        },
      },
      bankListItemDetails: {
        display: 'grid',
        gridAutoFlow: 'column',
        gridTemplateColumns: '1fr 150px',
        alignItems: 'center',
        gap: 20,
        [muiTheme.breakpoints.down('lg')]: {
          gridAutoFlow: 'row',
          gridTemplateColumns: '1fr 125px',
        },
        [muiTheme.breakpoints.down('md')]: {
          gridAutoFlow: 'row',
          gridTemplateColumns: '1fr',
        },
      },
      bankListItemContent: {
        display: 'grid',
        gridTemplateColumns: '1fr repeat(3, 0.75fr)',
        alignItems: 'center',
        minHeight: 120,
        gap: 20,
        [muiTheme.breakpoints.down('md')]: {
          gridTemplateColumns: '1fr 1fr',
        },
      },
    }),
)

const BankListItem = ({
  bankListItem,
  isRowExpanded,
  expandHandler,
}: IBankListItemProps) => {
  const { bankListItemContainer, bankListItemDetails, bankListItemContent } =
    useStyles({
      isRowExpanded,
    })
  const { product, calculation } = bankListItem

  return (
    <div className={bankListItemContainer}>
      <div className={bankListItemDetails}>
        <div className={bankListItemContent}>
          <BankLogoWithRating
            imageSources={product.images}
            isRowExpanded={isRowExpanded}
            recommendationRate={product.recommendationRate}
          />

          <BankRate
            rate={convertToGermanCurrencyFormat(calculation.monthlyRate)}
            descriptionText='MONATSRATE'
            decorator={{
              affix: RATE_AFFIX.SUFFIX,
              value: '€',
            }}
          />

          <BankRate
            rate={convertToGermanLocale(calculation.nominalInterestRate)}
            descriptionText='SOLLZINS GEB. P.A.'
            decorator={{
              affix: RATE_AFFIX.SUFFIX,
              value: '%',
            }}
            microText={{
              value: convertToGermanLocale(calculation.nominalInterestRateMax),
            }}
          />

          <BankRate
            rate={convertToGermanLocale(calculation.effectiveInterestRate)}
            descriptionText='EFF. JAHRESZINS'
            decorator={{
              affix: RATE_AFFIX.SUFFIX,
              value: '%',
            }}
            microText={{
              value: convertToGermanLocale(
                calculation.effectiveInterestRateMax,
              ),
            }}
            specialInterestRate={{
              isSpecial: calculation.specialInterest,
              order: -1,
              themeColors: {
                bgColor: theme.palette.primary.light,
                color: theme.palette.primary.main,
              },
            }}
          />
        </div>
        <BankListCTA
          isRowExpanded={isRowExpanded}
          expandHandler={expandHandler}
          productId={`${product.id}`}
        />
      </div>
    </div>
  )
}

export default memo(BankListItem)
